import { Dialog } from '@headlessui/react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useGoldenDemands } from '../../hooks/useGoldenDemands';
import axiosInstance from '../../services/axiosConfig';
import {
  createEvaluationBatch,
  MatterEvaluation,
} from '../../services/evaluationService';
import { Button } from '../ui/catalyst/button';
import {
  ComboboxOption,
  ComboboxWithClear,
} from '../ui/catalyst/combobox-with-clear';
import { Input } from '../ui/catalyst/input';
import { Select } from '../ui/catalyst/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/catalyst/table';
import { Text } from '../ui/catalyst/text';
import { Textarea } from '../ui/catalyst/textarea';
import { Label } from '../ui/label';

interface CreateEvaluationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateComplete: () => void;
}

interface AvailableDocument {
  matter_id: string;
  matter_name: string;
  document_id: string;
  document_name: string;
  workspace_id: string;
  created_at: string;
  modified_at: string;
}

interface MatterWithDocuments {
  matter_id: string;
  matter_name: string;
  documents: AvailableDocument[];
}

export const CreateEvaluationModal = ({
  isOpen,
  onClose,
  onCreateComplete,
}: CreateEvaluationModalProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [matterEvaluations, setMatterEvaluations] = useState<
    MatterEvaluation[]
  >([]);
  const [creating, setCreating] = useState(false);
  const [runProofreading, setRunProofreading] = useState(true);
  const [waitForGeneration, setWaitForGeneration] = useState(true);
  const [generationTimeoutSeconds, setGenerationTimeoutSeconds] = useState(180);
  const [templateIdsByMatter, setTemplateIdsByMatter] = useState<
    Record<string, string>
  >({});
  const [availableExamples, setAvailableExamples] = useState<
    Array<{ id: string; title: string }>
  >([]);
  const [templatesByExample, setTemplatesByExample] = useState<
    Record<string, Array<{ id: string; name: string }>>
  >({});
  const [selectedExampleByMatter, setSelectedExampleByMatter] = useState<
    Record<string, string>
  >({});
  const [selectedTemplateIdsByMatter, setSelectedTemplateIdsByMatter] =
    useState<Record<string, string[]>>({});
  const [sourceModeByMatter, setSourceModeByMatter] = useState<
    Record<string, 'existing' | 'generate'>
  >({});

  const [, setAvailableDocuments] = useState<AvailableDocument[]>([]);
  const [matterGroups, setMatterGroups] = useState<MatterWithDocuments[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [expandedMatters, setExpandedMatters] = useState<Set<string>>(
    new Set(),
  );

  const { goldenDemands } = useGoldenDemands();

  // Fetch available documents and group by matter
  useEffect(() => {
    const fetchDocuments = async () => {
      setLoadingDocuments(true);
      try {
        const response = await axiosInstance.get('/api/available-documents/');
        const documents = response.data.documents || [];
        const apiMatters = response.data.matters || [];
        setAvailableDocuments(documents);

        // Group documents by matter
        const matterMap = new Map<string, MatterWithDocuments>();
        documents.forEach((doc: AvailableDocument) => {
          if (!matterMap.has(doc.matter_id)) {
            matterMap.set(doc.matter_id, {
              matter_id: doc.matter_id,
              matter_name: doc.matter_name,
              documents: [],
            });
          }
          matterMap.get(doc.matter_id)!.documents.push(doc);
        });

        // Ensure all matters are represented even if they have zero documents
        apiMatters.forEach((m: { matter_id: string; matter_name: string }) => {
          if (!matterMap.has(m.matter_id)) {
            matterMap.set(m.matter_id, {
              matter_id: m.matter_id,
              matter_name: m.matter_name,
              documents: [],
            });
          }
        });

        setMatterGroups(Array.from(matterMap.values()));
      } catch (error) {
        console.error('Error fetching documents:', error);
        toast.error('Failed to load documents');
      } finally {
        setLoadingDocuments(false);
      }
    };

    if (isOpen) {
      fetchDocuments();
    }
  }, [isOpen]);

  // Fetch demand examples and their templates for selection
  useEffect(() => {
    const fetchExamplesAndTemplates = async () => {
      try {
        // All processed examples, minimal fields
        const resp = await axiosInstance.get(
          '/api/demands/examples/?limit=1000',
        );
        const results = (resp.data?.results || []) as Array<{
          id: string;
          title: string;
        }>;
        setAvailableExamples(results);

        // Fetch templates for each example concurrently
        const settled = await Promise.allSettled(
          results.map((ex) =>
            axiosInstance
              .get(`/api/demand-examples/${ex.id}/templates/`)
              .then((tResp) => ({
                exId: ex.id,
                templates: (tResp.data || []) as Array<{
                  id: string;
                  name: string;
                }>,
              })),
          ),
        );

        const entries: Record<string, Array<{ id: string; name: string }>> = {};
        settled.forEach((res) => {
          if (res.status !== 'fulfilled') return;
          const { exId, templates } = res.value;
          if (Array.isArray(templates) && templates.length > 0) {
            entries[exId] = templates.map((t) => ({ id: t.id, name: t.name }));
          }
        });
        setTemplatesByExample(entries);
      } catch (e) {
        // Ignore errors silently in this optional helper
      }
    };

    if (isOpen) {
      fetchExamplesAndTemplates();
    }
  }, [isOpen]);

  // Validate per matter mode: existing -> needs docs; generate -> needs templates
  const hasValidSelections = matterEvaluations.every((me) => {
    if (!me.gold_standard_id) return false;
    const entered = (templateIdsByMatter[me.matter_id] || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const selectedTemplates = selectedTemplateIdsByMatter[me.matter_id] || [];
    const mode = sourceModeByMatter[me.matter_id] || 'existing';
    if (mode === 'existing') {
      return me.document_selections.length > 0;
    }
    return entered.length > 0 || selectedTemplates.length > 0;
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name || matterEvaluations.length === 0) {
      toast.error('Please fill in name and select at least one matter');
      return;
    }

    if (!hasValidSelections) {
      toast.error(
        'Please select a golden demand and at least one document for each matter',
      );
      return;
    }

    setCreating(true);

    try {
      const generate_from_templates: Array<{
        matter_id: string;
        template_id: string;
      }> = [];
      matterEvaluations.forEach((me) => {
        const mode = sourceModeByMatter[me.matter_id] || 'existing';
        if (mode !== 'generate') return;
        const raw = templateIdsByMatter[me.matter_id] || '';
        const ids = raw
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        const selected = selectedTemplateIdsByMatter[me.matter_id] || [];
        [...ids, ...selected].forEach((tid) =>
          generate_from_templates.push({
            matter_id: me.matter_id,
            template_id: tid,
          }),
        );
      });
      const anyGenerating = matterEvaluations.some(
        (me) => (sourceModeByMatter[me.matter_id] || 'existing') === 'generate',
      );
      const finalWaitForGeneration = anyGenerating ? waitForGeneration : false;
      await createEvaluationBatch({
        name,
        description,
        matter_evaluations: matterEvaluations,
        options: {
          run_proofreading: runProofreading,
          wait_for_generation: finalWaitForGeneration,
          generation_timeout_seconds: generationTimeoutSeconds,
          generate_from_templates,
        },
      });

      onCreateComplete();

      // Reset form
      setName('');
      setDescription('');
      setMatterEvaluations([]);
      setTemplateIdsByMatter({});
    } catch (error) {
      console.error('Error creating evaluation batch:', error);
      toast.error('Failed to create evaluation batch');
    } finally {
      setCreating(false);
    }
  };

  // Add a matter to the evaluation
  const addMatterEvaluation = (matter: MatterWithDocuments) => {
    const existingMatter = matterEvaluations.find(
      (me) => me.matter_id === matter.matter_id,
    );
    if (!existingMatter) {
      setMatterEvaluations((prev) => [
        ...prev,
        {
          matter_id: matter.matter_id,
          matter_name: matter.matter_name,
          gold_standard_id: '',
          document_selections: [],
        },
      ]);
    }
  };

  // Remove a matter from the evaluation
  const removeMatterEvaluation = (matterId: string) => {
    setMatterEvaluations((prev) =>
      prev.filter((me) => me.matter_id !== matterId),
    );
  };

  // Update the golden demand for a matter
  const updateMatterGoldStandard = (
    matterId: string,
    goldStandardId: string,
  ) => {
    setMatterEvaluations((prev) =>
      prev.map((me) =>
        me.matter_id === matterId
          ? { ...me, gold_standard_id: goldStandardId }
          : me,
      ),
    );
  };

  // Toggle document selection for a matter
  const toggleDocumentForMatter = (
    matterId: string,
    doc: AvailableDocument,
  ) => {
    setMatterEvaluations((prev) =>
      prev.map((me) => {
        if (me.matter_id === matterId) {
          const isSelected = me.document_selections.some(
            (ds) => ds.document_id === doc.document_id,
          );
          if (isSelected) {
            return {
              ...me,
              document_selections: me.document_selections.filter(
                (ds) => ds.document_id !== doc.document_id,
              ),
            };
          }
          return {
            ...me,
            document_selections: [
              ...me.document_selections,
              {
                document_id: doc.document_id,
                document_name: doc.document_name,
              },
            ],
          };
        }
        return me;
      }),
    );
  };

  const toggleMatterExpansion = (matterId: string) => {
    setExpandedMatters((prev) => {
      const next = new Set(prev);
      if (next.has(matterId)) {
        next.delete(matterId);
      } else {
        next.add(matterId);
      }
      return next;
    });
  };

  // ------------------ Generation helpers (per-matter) ------------------
  const handleSourceModeChange = (
    matterId: string,
    mode: 'existing' | 'generate',
  ) => {
    setSourceModeByMatter((prev) => ({ ...prev, [matterId]: mode }));
  };

  const handleExampleSelect = (matterId: string, exampleId: string) => {
    setSelectedExampleByMatter((prev) => ({ ...prev, [matterId]: exampleId }));
    // Reset picked templates when switching example
    setSelectedTemplateIdsByMatter((prev) => ({ ...prev, [matterId]: [] }));
  };

  const handleTemplateToggle = (matterId: string, templateId: string) => {
    setSelectedTemplateIdsByMatter((prev) => {
      const curr = new Set(prev[matterId] || []);
      if (curr.has(templateId)) {
        curr.delete(templateId);
      } else {
        curr.add(templateId);
      }
      return { ...prev, [matterId]: Array.from(curr) };
    });
  };

  const handleTemplateMultiSelect = (
    matterId: string,
    templateIds: string[],
  ) => {
    setSelectedTemplateIdsByMatter((prev) => ({
      ...prev,
      [matterId]: templateIds,
    }));
  };

  const handleTemplateIdsInput = (matterId: string, value: string) => {
    setTemplateIdsByMatter((prev) => ({ ...prev, [matterId]: value }));
  };

  // Get the total number of selected documents across all matters
  const getTotalSelectedDocuments = () => {
    return matterEvaluations.reduce(
      (total, me) => total + me.document_selections.length,
      0,
    );
  };

  const getTotalTemplateIdsEntered = () => {
    return matterEvaluations.reduce((total, me) => {
      const raw = templateIdsByMatter[me.matter_id] || '';
      const ids = raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      return total + ids.length;
    }, 0);
  };

  const getTotalSelectedTemplatesPicked = () => {
    return matterEvaluations.reduce((total, me) => {
      const picked = selectedTemplateIdsByMatter[me.matter_id] || [];
      return total + picked.length;
    }, 0);
  };

  const totalSources =
    getTotalSelectedDocuments() +
    getTotalTemplateIdsEntered() +
    getTotalSelectedTemplatesPicked();
  const isGeneratingSelected =
    getTotalTemplateIdsEntered() + getTotalSelectedTemplatesPicked() > 0;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start mb-4">
              <Dialog.Title className="text-lg font-semibold">
                Create Evaluation Batch
              </Dialog.Title>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div>
                <Label htmlFor="name">Batch Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Q1 2024 Demand Quality Evaluation"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Purpose and scope of this evaluation batch"
                  rows={2}
                />
              </div>

              {/* Matter Selection and Configuration */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-base font-medium">
                    Select Matters & Configure Evaluations
                  </Label>
                  <Text className="text-sm text-gray-600">
                    {matterEvaluations.length} matters selected
                  </Text>
                </div>

                {loadingDocuments ? (
                  <div className="text-center py-8">
                    <Text className="text-gray-500">
                      Loading matters and documents...
                    </Text>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Matter Search and Add */}
                    {matterGroups.length > 0 && (
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <Label className="font-medium mb-2 block">
                          Search and add matters
                        </Label>
                        <ComboboxWithClear
                          options={matterGroups}
                          displayValue={(o: MatterWithDocuments | null) =>
                            o?.matter_name
                          }
                          placeholder="Type to search matters..."
                          aria-label="Search and add matters"
                          onChange={(option: MatterWithDocuments | null) => {
                            if (option) addMatterEvaluation(option);
                          }}
                          className="w-full"
                          panelClassName="max-h-64"
                        >
                          {(option: MatterWithDocuments) => (
                            <ComboboxOption
                              key={option.matter_id}
                              value={option}
                              className="group flex cursor-pointer items-center justify-between gap-3 rounded-lg px-3 py-2 data-[focus]:bg-blue-50"
                            >
                              <span className="text-sm">
                                {option.matter_name}
                              </span>
                              <span className="text-xs text-gray-500">
                                {option.documents.length} docs
                              </span>
                            </ComboboxOption>
                          )}
                        </ComboboxWithClear>
                      </div>
                    )}

                    {/* Selected Matters Table */}
                    {matterEvaluations.length > 0 && (
                      <div className="border rounded-lg">
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableHeader>Matter</TableHeader>
                              <TableHeader>Golden Demand</TableHeader>
                              <TableHeader>Documents</TableHeader>
                              <TableHeader>Actions</TableHeader>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {matterEvaluations.map((me) => {
                              const matterDocs =
                                matterGroups.find(
                                  (mg) => mg.matter_id === me.matter_id,
                                )?.documents || [];
                              const isExpanded = expandedMatters.has(
                                me.matter_id,
                              );
                              return (
                                <>
                                  <TableRow key={me.matter_id}>
                                    <TableCell>
                                      <Text className="font-medium">
                                        {me.matter_name}
                                      </Text>
                                    </TableCell>
                                    <TableCell>
                                      <Select
                                        value={me.gold_standard_id}
                                        onChange={(e) =>
                                          updateMatterGoldStandard(
                                            me.matter_id,
                                            e.target.value,
                                          )
                                        }
                                        required
                                      >
                                        <option value="">
                                          Select a golden demand...
                                        </option>
                                        {Array.isArray(goldenDemands) &&
                                          goldenDemands.map((gs) => (
                                            <option key={gs.id} value={gs.id}>
                                              {gs.name}{' '}
                                              {gs.processed
                                                ? '✓'
                                                : '(Processing...)'}
                                            </option>
                                          ))}
                                      </Select>
                                    </TableCell>
                                    <TableCell>
                                      <div className="flex items-center gap-2">
                                        <Text className="text-sm">
                                          {me.document_selections.length} of{' '}
                                          {matterDocs.length} selected
                                        </Text>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            toggleMatterExpansion(me.matter_id)
                                          }
                                          className="text-blue-600 hover:text-blue-800"
                                          aria-expanded={isExpanded}
                                          aria-label="Configure documents"
                                        >
                                          {isExpanded ? (
                                            <ChevronDownIcon className="h-4 w-4 inline" />
                                          ) : (
                                            <ChevronRightIcon className="h-4 w-4 inline" />
                                          )}
                                        </button>
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          removeMatterEvaluation(me.matter_id)
                                        }
                                        className="text-red-600 hover:text-red-700"
                                        aria-label={`Remove ${me.matter_name}`}
                                      >
                                        ×
                                      </button>
                                    </TableCell>
                                  </TableRow>
                                  {isExpanded && (
                                    <TableRow key={`${me.matter_id}-expanded`}>
                                      <td colSpan={4} className="p-0">
                                        <div className="p-4 border-t">
                                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {/* Left: Use existing documents */}
                                            <div>
                                              <Label className="flex items-center gap-2">
                                                <input
                                                  type="radio"
                                                  name={`src-${me.matter_id}`}
                                                  checked={
                                                    (sourceModeByMatter[
                                                      me.matter_id
                                                    ] || 'existing') ===
                                                    'existing'
                                                  }
                                                  onChange={() =>
                                                    handleSourceModeChange(
                                                      me.matter_id,
                                                      'existing',
                                                    )
                                                  }
                                                />
                                                Use existing documents
                                              </Label>
                                              <div className="border rounded-md max-h-64 overflow-y-auto mt-2">
                                                {matterDocs.map((doc) => {
                                                  const isSelected =
                                                    me.document_selections.some(
                                                      (ds) =>
                                                        ds.document_id ===
                                                        doc.document_id,
                                                    );
                                                  return (
                                                    <div
                                                      key={doc.document_id}
                                                      className={`flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 ${isSelected ? 'bg-blue-50' : ''}`}
                                                      role="button"
                                                      tabIndex={0}
                                                      aria-pressed={isSelected}
                                                      onClick={() =>
                                                        toggleDocumentForMatter(
                                                          me.matter_id,
                                                          doc,
                                                        )
                                                      }
                                                      onKeyDown={(e) => {
                                                        if (
                                                          e.key === 'Enter' ||
                                                          e.key === ' '
                                                        ) {
                                                          e.preventDefault();
                                                          toggleDocumentForMatter(
                                                            me.matter_id,
                                                            doc,
                                                          );
                                                        }
                                                      }}
                                                    >
                                                      <div className="flex items-center gap-3">
                                                        <input
                                                          type="checkbox"
                                                          checked={isSelected}
                                                          onChange={() => {}}
                                                          className="h-4 w-4 text-blue-600"
                                                        />
                                                        <DocumentIcon className="h-5 w-5 text-gray-400" />
                                                        <div>
                                                          <Text className="font-medium">
                                                            {doc.document_name}
                                                          </Text>
                                                          <Text className="text-xs text-gray-500">
                                                            Modified:{' '}
                                                            {new Date(
                                                              doc.modified_at,
                                                            ).toLocaleDateString()}
                                                          </Text>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  );
                                                })}
                                                {matterDocs.length === 0 && (
                                                  <div className="text-center py-4">
                                                    <Text className="text-gray-500">
                                                      No documents available
                                                    </Text>
                                                  </div>
                                                )}
                                              </div>
                                            </div>

                                            {/* Right: Generate from template(s) */}
                                            <div>
                                              <Label className="flex items-center gap-2">
                                                <input
                                                  type="radio"
                                                  name={`src-${me.matter_id}`}
                                                  checked={
                                                    (sourceModeByMatter[
                                                      me.matter_id
                                                    ] || 'existing') ===
                                                    'generate'
                                                  }
                                                  onChange={() =>
                                                    handleSourceModeChange(
                                                      me.matter_id,
                                                      'generate',
                                                    )
                                                  }
                                                />
                                                Generate draft from template(s)
                                              </Label>
                                              <div className="mt-2 space-y-3">
                                                <div>
                                                  <Label className="text-xs">
                                                    Pick example (optional)
                                                  </Label>
                                                  <Select
                                                    value={
                                                      selectedExampleByMatter[
                                                        me.matter_id
                                                      ] || ''
                                                    }
                                                    onChange={(e) =>
                                                      handleExampleSelect(
                                                        me.matter_id,
                                                        e.target.value,
                                                      )
                                                    }
                                                  >
                                                    <option value="">—</option>
                                                    {availableExamples.map(
                                                      (ex) => (
                                                        <option
                                                          key={ex.id}
                                                          value={ex.id}
                                                        >
                                                          {ex.title}
                                                        </option>
                                                      ),
                                                    )}
                                                  </Select>
                                                </div>
                                                {(() => {
                                                  const exId =
                                                    selectedExampleByMatter[
                                                      me.matter_id
                                                    ] || '';
                                                  const list =
                                                    templatesByExample[exId] ||
                                                    [];
                                                  if (!list.length) return null;
                                                  return (
                                                    <div>
                                                      <Label className="text-xs">
                                                        Select template(s)
                                                      </Label>
                                                      <select
                                                        multiple
                                                        className="mt-1 block w-full border rounded-md max-h-48 overflow-y-auto text-sm"
                                                        value={
                                                          selectedTemplateIdsByMatter[
                                                            me.matter_id
                                                          ] || []
                                                        }
                                                        onChange={(e) =>
                                                          handleTemplateMultiSelect(
                                                            me.matter_id,
                                                            Array.from(
                                                              e.target
                                                                .selectedOptions,
                                                            ).map(
                                                              (o) => o.value,
                                                            ),
                                                          )
                                                        }
                                                      >
                                                        {list.map((t) => (
                                                          <option
                                                            key={t.id}
                                                            value={t.id}
                                                          >
                                                            {t.name}
                                                          </option>
                                                        ))}
                                                      </select>
                                                    </div>
                                                  );
                                                })()}
                                                <div>
                                                  <Label className="text-xs">
                                                    Or enter template IDs
                                                    (comma-separated)
                                                  </Label>
                                                  <Input
                                                    value={
                                                      templateIdsByMatter[
                                                        me.matter_id
                                                      ] || ''
                                                    }
                                                    onChange={(e) =>
                                                      handleTemplateIdsInput(
                                                        me.matter_id,
                                                        e.target.value,
                                                      )
                                                    }
                                                    placeholder="template-id-1, template-id-2"
                                                  />
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                    </TableRow>
                                  )}
                                </>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    )}

                    {matterEvaluations.length === 0 &&
                      matterGroups.length > 0 && (
                        <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                          <Text className="text-gray-500">
                            Select matters above to configure their evaluations
                          </Text>
                        </div>
                      )}

                    {matterGroups.length === 0 && !loadingDocuments && (
                      <div className="text-center py-8">
                        <Text className="text-gray-500">
                          No matters with documents available
                        </Text>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Generation Options + Submit */}
              <div className="border-t pt-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <label
                    className="flex items-center gap-2"
                    htmlFor="run-proofreading"
                  >
                    <input
                      id="run-proofreading"
                      type="checkbox"
                      checked={runProofreading}
                      onChange={(e) => setRunProofreading(e.target.checked)}
                    />
                    <span className="text-sm">
                      Run proofreading on generation
                    </span>
                  </label>
                  <label
                    className="flex items-center gap-2"
                    htmlFor="wait-for-generation"
                  >
                    <input
                      id="wait-for-generation"
                      type="checkbox"
                      checked={isGeneratingSelected ? waitForGeneration : false}
                      onChange={(e) => setWaitForGeneration(e.target.checked)}
                      disabled={!isGeneratingSelected}
                    />
                    <span className="text-sm">Wait for drafts to be ready</span>
                  </label>
                  {/* Timeout removed per requirements */}
                </div>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" plain onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    creating ||
                    !name ||
                    matterEvaluations.length === 0 ||
                    totalSources === 0 ||
                    !hasValidSelections
                  }
                >
                  {creating
                    ? 'Creating...'
                    : `Create Batch (${totalSources} sources)`}
                </Button>
              </div>
            </form>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};
